import { Button, DialogActions, DialogContent } from "@mui/material";
import { PlanId } from "components/common/boundary/PlanId";
import { useMaterials } from "contexts/search/SearchContextProvider";
import { useTenantTranslation } from "hooks/formatters";
import { useAppDispatch, useAppSelector } from "src/store/store";
import React, { useCallback, useState } from "react";
import {
  useGetPlanResultsQuery,
  PlannedResultsRead,
  MaterialRead,
  generatedApi,
  useUpdatePlanResultsMutation,
  ChildMixUpdate,
  NameBasedPeriodMaterialSummary,
  SteelGrade,
} from "src/store/api/generatedApi";
import { useTenant } from "hooks/settings";
import { EditableTableSkeleton } from "components/common/plan/EditableTableSkeleton";
import { EditableTableActions } from "components/common/plan/EditableTableActions";
import { DeployTable } from "components/common/plan/DeployTable";
import { filterPlanResultsToPeriod } from "components/common/plan/utils";
import dayjs from "dayjs";
import { useMixGrouping } from "hooks/useMixGrouping";
import { minimumPeriod } from "hooks/periodIndex";
import { typeSafeObjectFromEntries } from "src/utils/typeSafeObjectFromEntries";
import {
  selectSearchedSteelGradeIds,
  steelGradeSearchSlice,
} from "src/store/slices/steelGradeSearchSlice";
import { useSearchId } from "hooks/search";
import { CancelButton } from "../exportPlan/cancelButton";

interface Props {
  toNextState: () => void;
  planId: PlanId;
  setPlanId: (planId: PlanId) => void;
  doClose: () => void;
}

export const Edit = ({ toNextState, planId, setPlanId, doClose }: Props) => {
  const tenantName = useTenant();
  const planResults = useGetPlanResultsQuery({ tenantName, planId, period: 1 });
  const materials = useMaterials();

  if (materials.status === "success" && planResults.currentData) {
    return (
      <EditContent
        planId={planId}
        toNextState={toNextState}
        setPlanId={setPlanId}
        planResults={planResults.currentData}
        materials={materials.data.byIndex}
        periodMaterialSummaries={
          planResults.currentData.summary.period_material_summary
        }
        steelGrades={planResults.currentData.steel_grade_set.steel_grades}
        doClose={doClose}
      />
    );
  }
  return <EditableTableSkeleton />;
};

interface EditContentProps {
  planId: PlanId;
  planResults: PlannedResultsRead;
  materials: MaterialRead[];
  setPlanId: (planId: PlanId) => void;
  toNextState: () => void;
  periodMaterialSummaries: NameBasedPeriodMaterialSummary[];
  steelGrades: SteelGrade[];
  doClose: () => void;
}

export const EditContent = ({
  planId,
  planResults,
  materials,
  setPlanId,
  toNextState,
  periodMaterialSummaries,
  steelGrades,
  doClose,
}: EditContentProps) => {
  const tenantName = useTenant();
  const { t } = useTenantTranslation();
  const dispatch = useAppDispatch();
  const searchId = useSearchId();

  const period = minimumPeriod;
  const periodResults = React.useMemo(
    () => planResults.periods[period.toString()]?.mixes ?? [],
    [planResults, period]
  );

  const [defaultStartAt] = useState(() => new Date());
  const startAt = planResults.start_at
    ? new Date(planResults.start_at)
    : defaultStartAt;
  const timeString = dayjs(startAt).format("MMM YYYY");

  const [doUpdatePlanResultsMutation] = useUpdatePlanResultsMutation();
  const [getResult] = generatedApi.useLazyGetResultQuery();

  const handleOnSubmit = useCallback(
    async (updates: ChildMixUpdate[], comment: string) => {
      const { data: updated_plan, error } = await doUpdatePlanResultsMutation({
        tenantName,
        planId: planResults.plan_id,
        planMixUpdates: {
          period_updates: [
            {
              period: period,
              updates,
              replacements: {},
            },
          ],
          comment: comment,
        },
      });
      if (error === undefined) {
        void dispatch(
          generatedApi.util.upsertQueryData(
            "getPlanResults",
            { planId: updated_plan.plan_id, tenantName, period },
            filterPlanResultsToPeriod(updated_plan, period)
          )
        );

        dispatch(
          steelGradeSearchSlice.actions.copy([
            planResults.plan_id as PlanId,
            period,
            "deploy",
            updated_plan.plan_id as PlanId,
            period,
          ])
        );

        if (searchId === null) {
          throw new Error(
            `Search id should not be null in context with plan id ${planResults.plan_id}`
          );
        }

        await getResult({ searchId, tenantName });
        setPlanId(updated_plan.plan_id as PlanId);
      }
    },
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- getResult is an async helper with unstable identity; including it risks recreating the callback on every render
    [
      searchId,
      doUpdatePlanResultsMutation,
      tenantName,
      planResults,
      setPlanId,
      dispatch,
    ]
  );

  const mixGrouping = useMixGrouping();

  const inventoryByMaterialName = React.useMemo(
    () =>
      typeSafeObjectFromEntries(
        periodMaterialSummaries
          .filter((summary) => summary.period === period)
          .map((summary) => [
            summary.material,
            summary.initial_inventory + summary.obtainable_obtained,
          ])
      ),
    [periodMaterialSummaries, period]
  );

  const filteredSteelGrades = React.useMemo(() => {
    const steelGradeNames = new Set(
      periodResults.flatMap((result) =>
        result.production.map((item) => item.steel_grade_name)
      )
    );
    return steelGrades.filter((steelGrade) =>
      steelGradeNames.has(steelGrade.name)
    );
  }, [periodResults, steelGrades]);

  const tableMaterials = React.useMemo(() => {
    return materials
      .filter((material) => material.addition_location === "basket")
      .sort(
        (materialA, materialB) =>
          materialA.deployment_ordering - materialB.deployment_ordering
      )
      .map((material) => ({
        id: material.id,
        name: material.name,
        inventory: inventoryByMaterialName[material.name] ?? null,
      }));
  }, [materials, inventoryByMaterialName]);

  const driInventory = materials.some(
    (material) => material.addition_location === "hopper"
  )
    ? materials
        .filter((material) => material.addition_location === "hopper")
        .reduce(
          (total, material) =>
            total + (inventoryByMaterialName[material.name] ?? 0),
          0
        )
    : null;

  const selectedSteelGradeIds = useAppSelector((state) =>
    selectSearchedSteelGradeIds(state, planResults.plan_id, period, "deploy")
  );

  return (
    <>
      <DialogContent>
        <EditableTableActions
          timeString={timeString}
          view="deploy"
          planId={planResults.plan_id as PlanId}
          period={period}
          materials={materials}
          showDriConsumption={driInventory !== null}
          steelGrades={filteredSteelGrades}
          onSubmit={handleOnSubmit}
          results={periodResults}
        >
          {(onChange, _, isSubmitting, hasEdited, rows) => (
            <DeployTable
              planId={planResults.plan_id as PlanId}
              period={period}
              disableEdit={isSubmitting}
              hasEdited={hasEdited}
              rows={rows}
              materials={tableMaterials}
              selectedSteelGradeIds={selectedSteelGradeIds}
              onChange={onChange}
              mixGrouping={mixGrouping}
              driInventory={driInventory}
              steelGradesWidth={210}
            />
          )}
        </EditableTableActions>
      </DialogContent>
      <DialogActions>
        <CancelButton planId={planId} doClose={doClose} />
        <Button variant="contained" onClick={toNextState}>
          {t("next")}
        </Button>
      </DialogActions>
    </>
  );
};
